import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  DashboardOutlined,
  EditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  LinkOutlined,
  LogoutOutlined,
  MenuOutlined,
  PlusOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Drawer, Dropdown, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { useAuth } from "@/Context/AuthContext";
import { UserContext } from "@/Context/UserContext";
import HeadingIcon from "../../Assets/Images/Heading.png";

const { Text } = Typography;

// Sidebar Content Component (reusable for both desktop and mobile)
const SidebarContent = ({ user, navigate, fetch, onItemClick, resumeId, isAuthenticated, isAdminAccess }) => {
  const userMenuItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      onClick: () => {
        navigate("/app/dashboard");
        if (onItemClick) onItemClick();
      },
    },
    {
      key: "account",
      label: "Account",
      icon: <UserOutlined />,
      onClick: () => {
        navigate("/app/account");
        if (onItemClick) onItemClick();
      },
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: () => {
        localStorage.removeItem("userId");
        localStorage.removeItem("authorization");
        window.location.href = "/";
      },
    },
  ];

  return (
    <>
      {/* Sidebar Content */}
      <div
        style={{
          flex: 1,
          padding: "8px",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Expanded view - Show different menus based on auth and resume status */}
        <div style={{ width: "100%" }}>
          {isAuthenticated && resumeId ? (
            /* Case 1: Authenticated + Has Resume - Show MY RESUME menu */
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                  marginBottom: "12px",
                }}
              >
                <Text style={{ fontSize: "18px", marginRight: "8px" }}>📄</Text>
                <Text
                  style={{
                    fontWeight: "700",
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: "#374151",
                  }}
                >
                  MY RESUME
                </Text>
              </div>
              <div style={{ marginTop: "8px" }}>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    if (resumeId) {
                      navigate(`/app/resume/${resumeId}`);
                      if (onItemClick) onItemClick();
                    }
                  }}
                >
                  <EditOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Edit Resume</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    if (resumeId) {
                      navigate(`/app/resume-score/${resumeId}`);
                      if (onItemClick) onItemClick();
                    }
                  }}
                >
                  <ThunderboltOutlined style={{ marginRight: "8px", fontSize: "16px", color: "#722ed1" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Resume Score</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    if (resumeId) {
                      navigate(`/app/resume/${resumeId}/preview?template=classic`);
                      if (onItemClick) onItemClick();
                    }
                  }}
                >
                  <EyeOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Resume Preview</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    if (resumeId) {
                      navigate(`/app/resume/${resumeId}/edit-photo`);
                      if (onItemClick) onItemClick();
                    }
                  }}
                >
                  <UserOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Edit Photo</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    if (resumeId) {
                      navigate(`/app/resume/${resumeId}/preview?template=classic&share`);
                      if (onItemClick) onItemClick();
                    }
                  }}
                >
                  <ShareAltOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Share Resume Avatars</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={async () => {
                    if (resumeId) {
                      try {
                        const response = await fetch.get(`/resume/${resumeId}`);
                        if (response.success && response.data?.publicSlug) {
                          const publicUrl = `${window.location.origin}/r/${response.data.publicSlug}`;
                          window.open(publicUrl, "_blank");
                          if (onItemClick) onItemClick();
                        }
                      } catch (error) {
                        console.error("Error fetching resume:", error);
                      }
                    }
                  }}
                >
                  <LinkOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Public Resume Link</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/dashboard");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <DashboardOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>My Dashboard</Text>
                </div>
              </div>
            </div>
          ) : isAuthenticated && !resumeId ? (
            /* Case 2: Authenticated but No Resume - Show GET STARTED menu */
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                  marginBottom: "12px",
                }}
              >
                <Text style={{ fontSize: "18px", marginRight: "8px" }}>🚀</Text>
                <Text
                  style={{
                    fontWeight: "700",
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: "#374151",
                  }}
                >
                  GET STARTED
                </Text>
              </div>
              <div style={{ marginTop: "8px" }}>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/resume-choice");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <PlusOutlined style={{ marginRight: "8px", fontSize: "16px", color: "#1890ff" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Create Resume</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/resume-score");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <ThunderboltOutlined style={{ marginRight: "8px", fontSize: "16px", color: "#722ed1" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Check Resume Score</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/dashboard");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <DashboardOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>My Dashboard</Text>
                </div>
              </div>
            </div>
          ) : (
            /* Case 3: Not authenticated - Show Log In / Sign Up options */
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                  marginBottom: "12px",
                }}
              >
                <Text style={{ fontSize: "18px", marginRight: "8px" }}>👤</Text>
                <Text
                  style={{
                    fontWeight: "700",
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: "#374151",
                  }}
                >
                  ACCOUNT
                </Text>
              </div>
              <div style={{ marginTop: "8px" }}>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/login");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <LogoutOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Log In</Text>
                </div>
                <div
                  className="sidebar-item"
                  onClick={() => {
                    navigate("/app/signup");
                    if (onItemClick) onItemClick();
                  }}
                >
                  <UserOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                  <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>Sign Up</Text>
                </div>
              </div>
            </div>
          )}

          {/* Resources Section */}
          <div style={{ marginTop: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "8px",
                marginBottom: "12px",
              }}
            >
              <Text style={{ fontSize: "18px", marginRight: "8px" }}>📚</Text>
              <Text
                style={{
                  fontWeight: "700",
                  fontSize: "14px",
                  textTransform: "uppercase",
                  color: "#374151",
                }}
              >
                RESOURCES
              </Text>
            </div>
            <div style={{ marginTop: "8px" }}>
              <div
                className="sidebar-item"
                onClick={() => {
                  window.location.href = "/resume-templates/how-to-write-resume/";
                  if (onItemClick) onItemClick();
                }}
              >
                <FileTextOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>How to write a resume?</Text>
              </div>
              <div
                className="sidebar-item"
                onClick={() => {
                  window.location.href = "/cover-letter/how-to-write/";
                  if (onItemClick) onItemClick();
                }}
              >
                <FileDoneOutlined style={{ marginRight: "8px", fontSize: "16px" }} />
                <Text style={{ lineHeight: "1.4", fontSize: "14px" }}>How to write a cover letter?</Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Avatar Section - Bottom of Sidebar */}
      {isAdminAccess ? (
        /* Admin Access Mode Indicator */
        <div
          style={{
            padding: "12px",
            borderTop: "1px solid #e5e7eb",
            background: "#fff7e6",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px",
              borderRadius: "6px",
            }}
          >
            <Avatar
              size={32}
              style={{
                backgroundColor: "#fa8c16",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              A
            </Avatar>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "block",
                  color: "#d46b08",
                }}
              >
                Admin Mode
              </Text>
              <Text
                type="secondary"
                style={{
                  fontSize: "12px",
                  display: "block",
                }}
              >
                Viewing user resume
              </Text>
            </div>
          </div>
        </div>
      ) : user && (
        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          {/* Add Credits Button */}
          <div style={{ padding: "10px 12px 0" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              onClick={() => navigate("/app/account?tab=billing")}
              style={{
                height: "36px",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "13px",
                background: "#1890ff",
                border: "none",
              }}
            >
              Add Credits
            </Button>
          </div>

          <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="topLeft">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                borderRadius: "6px",
                transition: "background-color 0.2s",
              }}
              className="user-menu-trigger"
            >
              <Avatar
                size={32}
                style={{
                  backgroundColor: "#1890ff",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
                icon={user?.firstName ? undefined : <UserOutlined />}
              >
                {user?.firstName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user.name}
                </Text>
                <Text
                  style={{
                    fontSize: "12px",
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: (user?.creditBalance ?? 0) > 0 ? "#52c41a" : "#ff4d4f",
                    fontWeight: 500,
                  }}
                >
                  ⚡ {user?.creditBalance ?? 0} Credits
                </Text>
              </div>
            </div>
          </Dropdown>
        </div>
      )}

      <style>{`
        .sidebar-item {
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 2px;
          color: #374151;
          display: flex;
          align-items: center;
        }

        .sidebar-item:hover {
          background-color: #f3f4f6;
        }

        .sidebar-item.active {
          background-color: #e5e7eb;
        }

        .user-menu-trigger:hover {
          background-color: #f0f0f0;
        }
      `}</style>
    </>
  );
};

// Desktop Sidebar Component
const AppSidebar = ({ user, resumeId, isAuthenticated, isAdminAccess }) => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        width: isExpanded ? "250px" : "60px",
        height: "100vh",
        backgroundColor: "#ffffff",
        borderRight: "1px solid #e5e7eb",
        transition: "width 0.1s ease",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="desktop-sidebar"
    >
      {/* Header */}
      <div
        style={{
          padding: "12px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isExpanded ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={HeadingIcon}
              alt="Resume AI Builder"
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
              }}
            />
            <strong style={{ fontSize: "16px", color: "#374151" }}>Upresume.ai</strong>
          </div>
        ) : (
          <img
            src={HeadingIcon}
            alt="Resume AI Builder"
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "4px",
            }}
          />
        )}
      </div>

      {isExpanded ? (
        <SidebarContent user={user} navigate={navigate} fetch={fetch} resumeId={resumeId} isAuthenticated={isAuthenticated} isAdminAccess={isAdminAccess} />
      ) : (
        <div style={{ padding: "8px", textAlign: "center", marginTop: "20px" }}>
          <Button type="text" icon={<MenuOutlined />} onClick={() => setIsExpanded(true)} />
        </div>
      )}

      {/* Hover Toggle Arrow */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            right: "-12px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "24px",
            height: "40px",
            backgroundColor: "#ffffff",
            borderRadius: "0 8px 8px 0",
            border: "1px solid #e5e7eb",
            borderLeft: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
            zIndex: 10,
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span
            style={{
              fontSize: "12px",
              color: "#6b7280",
              fontWeight: "bold",
            }}
          >
            {isExpanded ? "‹" : "›"}
          </span>
        </div>
      )}

      <style>{`
        .sidebar-item {
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 2px;
          color: #374151;
        }

        .sidebar-item:hover {
          background-color: #f3f4f6;
        }

        .sidebar-item.active {
          background-color: #e5e7eb;
        }

        .user-menu-trigger:hover {
          background-color: #f0f0f0;
        }
      `}</style>
    </div>
  );
};

// Common App Layout Component
const AppLayout = ({ children }) => {
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isAuthenticated: authContextAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fetch = useAuthenticatedFetch();
  const { userProfileData } = useContext(UserContext);

  // Check for admin access token (from URL or sessionStorage)
  // Wrapped in try-catch: some browsers (e.g. Chrome Mobile in strict privacy mode,
  // or pages loaded in sandboxed iframes) throw a SecurityError on sessionStorage access.
  const urlParams = new URLSearchParams(location.search);
  const adminTokenFromUrl = urlParams.get("adminToken");
  const adminTokenFromSession = (() => {
    try {
      return typeof window !== 'undefined' ? sessionStorage.getItem("adminResumeAccessToken") : null;
    } catch {
      return null;
    }
  })();
  const isAdminAccess = !!(adminTokenFromUrl || adminTokenFromSession);

  // Check authentication from multiple sources for reliability
  const hasLocalAuth = typeof window !== 'undefined' &&
    localStorage.getItem("userId") &&
    localStorage.getItem("authorization");
  // Treat admin access as authenticated for sidebar display purposes
  const isAuthenticated = authContextAuthenticated || hasLocalAuth || !!userProfileData?._id || isAdminAccess;

  // Get resumeId from user profile OR from URL path OR from sessionStorage (for admin)
  // This ensures sidebar shows correctly when viewing a resume
  const getResumeIdFromUrl = () => {
    // Match /app/resume/{id} or /app/resume-score/{id}
    const pathMatch = location.pathname.match(/\/app\/resume(?:-score)?\/([a-zA-Z0-9]+)/);
    return pathMatch ? pathMatch[1] : null;
  };

  // For admin access, also check sessionStorage for stored resumeId
  const getAdminResumeId = () => {
    if (isAdminAccess) {
      try {
        return sessionStorage.getItem("adminResumeId");
      } catch {
        return null;
      }
    }
    return null;
  };

  const urlResumeId = getResumeIdFromUrl();

  // Store resumeId in sessionStorage when admin accesses a resume
  useEffect(() => {
    if (isAdminAccess && urlResumeId) {
      try {
        sessionStorage.setItem("adminResumeId", urlResumeId);
      } catch {
        // sessionStorage unavailable (privacy mode / sandboxed iframe) — silently skip
      }
    }
  }, [isAdminAccess, urlResumeId]);

  const resumeId = userProfileData?.resumeId || urlResumeId || getAdminResumeId();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check for logged-in user and fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const userId = localStorage.getItem("userId");
      const authToken = localStorage.getItem("authorization");

      if (userId && authToken) {
        try {
          const response = await fetch.get(`/user/dashboard/${userId}`);
          if (response.success && response.data.user) {
            const userData = response.data.user;
            setUser({
              id: userData._id,
              name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || "User",
              firstName: userData.firstName?.charAt(0)?.toUpperCase() || "U",
              email: userData.email,
              creditBalance: response.data.planInfo?.credits?.balance ?? userData.credits?.balance ?? 0,
            });
          }
        } catch (error) {
          console.error("Error fetching user data", error.message || error);
          setUser({
            id: userId,
            name: "User",
            firstName: "U",
          });
        }
      }
    };

    fetchUserData();
  }, []);

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        backgroundColor: "#ffffff",
        overflow: "hidden",
      }}
    >
      <style>{`
        body, html {
          margin: 0;
          padding: 0;
          height: 100vh;
          width: 100vw;
        }

        /* Custom scrollbar styling */
        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      {/* Desktop Sidebar (hidden on mobile) */}
      <AppSidebar user={user} resumeId={resumeId} isAuthenticated={isAuthenticated} isAdminAccess={isAdminAccess} />

      {/* Mobile Hamburger Menu Button */}
      {isMobile && (
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuOpen(true)}
          style={{
            position: "fixed",
            top: "12px",
            left: "12px",
            zIndex: 999,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={HeadingIcon}
              alt="Resume AI Builder"
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
              }}
            />
            <strong style={{ fontSize: "16px", color: "#374151" }}>Upresume.ai</strong>
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
        closeIcon={<CloseOutlined />}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <SidebarContent
            user={user}
            navigate={navigate}
            fetch={fetch}
            onItemClick={() => setMobileMenuOpen(false)}
            resumeId={resumeId}
            isAuthenticated={isAuthenticated}
            isAdminAccess={isAdminAccess}
          />
        </div>
      </Drawer>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default AppLayout;
